import React, { useMemo, useState } from 'react';
import { NotebookPen } from 'lucide-react';
import { useAlteracoesCadastro } from '../hooks/useAlteracoesCadastro';
import {
    alteracoesVigentes, descreverCampo, ehNovidade, textoSemMotivo, type AlteracaoCadastro,
} from '../lib/alteracoesCadastro';

// "Mudanças no cadastro" nas telas de cálculo e emissão do boleto (pedido do Felipe,
// 29/10/2026). O Financeiro altera o cadastro a pedido do Faturamento ou negocia um
// desconto novo; quem calcula e emite vê o que mudou desde o último boleto, quem
// mudou, quando e por quê. Vale o que está no cadastro — o motivo explica a diferença
// para o termo de adesão.

const dataHora = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const data = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

const Item = ({ a, novo }: { a: AlteracaoCadastro; novo: boolean }) => (
    <li className={`pl-3 border-l-2 ${novo ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-600'}`}>
        <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
            {novo ? (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-600 text-white dark:bg-blue-500">Novo</span>
            ) : null}
            {a.soNaFatura ? (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Só nesta fatura</span>
            ) : null}
        </div>
        <ul className="mt-1 space-y-0.5">
            {a.campos.map((c, i) => (
                <li key={i} className="text-sm font-semibold text-gray-900 dark:text-white break-words">{descreverCampo(c)}</li>
            ))}
        </ul>
        {a.motivo ? (
            <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Motivo: </span>“{a.motivo}”
            </p>
        ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{textoSemMotivo(a)}</p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {a.alteradoPor || (a.automatica ? 'Sistema' : 'Usuário não identificado')} · {dataHora(a.alteradoEm)}{a.onde ? ` · ${a.onde}` : ''}
        </p>
    </li>
);

const AlteracoesCadastroUc = ({ ucId, faturaId, ucCodigo, className = '' }: {
    ucId: number | string | null | undefined;
    faturaId?: number | string | null;
    ucCodigo?: string | null;
    /** Margem de fora (ex.: "mb-4"): sem nada para mostrar, o card não ocupa espaço. */
    className?: string;
}) => {
    const { alteracoes, ultimoBoletoEm, carregando, erro } = useAlteracoesCadastro(ucId, { faturaId, ucCodigo });
    const [verTudo, setVerTudo] = useState(false);

    const novidades = useMemo(
        () => alteracoesVigentes(alteracoes).filter(a => ehNovidade(a, ultimoBoletoEm)),
        [alteracoes, ultimoBoletoEm],
    );

    if (!ucId || (carregando && alteracoes.length === 0)) return null;
    if (erro) {
        return <p className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}>Não deu para ler as mudanças do cadastro desta UC: {erro}</p>;
    }
    if (alteracoes.length === 0) return null;

    const anteriores = alteracoes.length - novidades.length;
    const desde = ultimoBoletoEm ? `desde o último boleto (${data(ultimoBoletoEm)})` : 'antes da primeira cobrança';
    const botaoVerTudo = anteriores > 0 ? (
        <button type="button" onClick={() => setVerTudo(v => !v)} className="text-xs font-semibold text-primary dark:text-primary-light hover:underline">
            {verTudo ? 'Ver só as novidades' : `Ver histórico completo (${alteracoes.length})`}
        </button>
    ) : null;

    // Nada novo: uma linha só, para não competir com o resto da tela.
    if (novidades.length === 0 && !verTudo) {
        return (
            <div className={`px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-wrap items-center justify-between gap-2 ${className}`}>
                <p className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                    <NotebookPen size={14} className="text-gray-400 shrink-0" />
                    Nenhuma mudança no cadastro {desde}.
                </p>
                {botaoVerTudo}
            </div>
        );
    }

    const lista = verTudo ? alteracoes : novidades;
    return (
        <section
            aria-label="Mudanças no cadastro da UC"
            className={`p-4 rounded-2xl border ${novidades.length > 0
                ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-100 dark:border-blue-900/50'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'} ${className}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="flex gap-3 items-start">
                    <div className="p-2 bg-blue-500/10 rounded-xl text-blue-600 dark:text-blue-400 shrink-0">
                        <NotebookPen size={18} />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-blue-900 dark:text-blue-200">
                            {novidades.length > 0
                                ? `${novidades.length === 1 ? 'Mudança' : `${novidades.length} mudanças`} no cadastro ${desde}`
                                : 'Histórico de mudanças no cadastro'}
                        </h4>
                        <p className="text-xs text-blue-800/80 dark:text-blue-300/80 mt-0.5">
                            Vale o que está no cadastro, mesmo que o termo de adesão diga outra coisa. Confira quem mudou e por quê.
                        </p>
                    </div>
                </div>
                {botaoVerTudo}
            </div>
            <ul className="space-y-3">
                {lista.map(a => <Item key={a.chave} a={a} novo={ehNovidade(a, ultimoBoletoEm)} />)}
            </ul>
        </section>
    );
};

export default AlteracoesCadastroUc;
